import React, { useState, useRef, useContext, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import Uri from "jsuri";
import i18n from "i18n/i18n";
import axios from "axios";
import isEmpty from "lodash/isEmpty";
import { Input, Button, ConfirmModal, Select, Modal, Icons } from "@texada/ui";
import { Link } from "react-router-dom";
import { PasswordVisibility } from "./password/PasswordVisibility";
import LoginInfoDisplay from "./LoginInfoDisplay";
import LoginContext from "./LoginContext";
import {
  isPlayEnv,
  isValidNumber,
  getApiErrorMsg,
  getEnvConstant
} from "helpers/helper";
import {
  INTERNAL_EMAIL_ACCOUNT,
  SCS_EMAIL_ACCOUNT,
  TIS_AUX_TYPE,
  NON_UPGRADED_USER_URL,
  NON_UPGRADED_COMPANY_URL,
  GLOBAL_ENV
} from "constants/AppConstants";
import {
  getLoginError,
  isSCSGeneratePassword,
  LoginTenant,
  LoginData,
  getLoginSource
} from "./loginUtils";

function useGeneratePassword() {
  const [url, setUrl] = useState("");
  const [showGeneratePassword, setShowGeneratePassword] = useState(false);

  return {
    showGeneratePassword,
    setUrl(url: string) {
      setUrl(url);
      setShowGeneratePassword(true);
    },
    onConfirm() {
      window.open(url, "_blank");
      setUrl("");
      setShowGeneratePassword(false);
    },
    onCancel() {
      setUrl("");
      setShowGeneratePassword(false);
    }
  };
}

const preventDefault = callback => e => {
  e.preventDefault();
  callback();
};

enum LOGIN_STEP {
  ENTER_EMAIL = "enter_email",
  ENTER_PASSWORD = "enter_password"
}

const LoginForm = () => {
  const location = useLocation();
  const { companyId } = useParams();
  const query = new URLSearchParams(location.search);
  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{
    username?: string;
    password?: string;
    tenant?: string;
    migrateStatus?: string;
  }>({});
  const [isLoading, setLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const passwordInput = useRef(null);
  const loginError = localStorage.getItem("login-error");
  const { login, error, setError } = useContext(LoginContext);
  const generatePassword = useGeneratePassword();
  const [step, setStep] = useState(LOGIN_STEP.ENTER_EMAIL);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState<string | number>("");
  const [tenantId, setTenantId] = useState();
  const [isMigrated, setIsMigrated] = useState(false);
  const [isTenantOpen, setIsTenantOpen] = useState(false);
  const [callbackUrl, setCallbackUrl] = useState("");

  useEffect(() => {
    const emailParam = location.search.match(/email=([^&?]*)/);
    if (emailParam) {
      const email = decodeURIComponent(emailParam[1]);
      setUserName(email);
    }
    const mobileRedirectUrl =
      query.get("callbackUrl") || localStorage.getItem("mobileRedirectUrl");
    if (mobileRedirectUrl) {
      setCallbackUrl(mobileRedirectUrl);
    }
    if (query.get("tenant")) {
      setSelectedTenant(query.get("tenant"));
    }
  }, []);

  useEffect(() => {
    if (!query.get("email") && !query.get("tenant")) return;
    if (username && !selectedTenant) {
      checkEmail();
    }
    if (username && selectedTenant) {
      setStep(LOGIN_STEP.ENTER_PASSWORD);
    }
  }, [username, selectedTenant]);

  useEffect(() => {
    if (!isValidNumber(selectedTenant) && tenantOptions.length < 1) return;
    const target = tenantOptions.find(e => e.value === selectedTenant);
    if (target && target.tenantId) {
      setTenantId(target.tenantId);
    }
  }, [selectedTenant, tenantOptions]);

  const isValid = (check = null) => {
    let errors: any = {};

    if (check !== "password") {
      if (isEmpty(username)) {
        errors.username = i18n.t("email_is_required");
      }

      const { aux } = getEmailAndAux();
      if (!isEmpty(aux) && (aux.length > 1 || !isValidNumber(aux))) {
        errors.username = i18n.t("invalid_email_address");
      }
    }

    if (check !== "username" && isEmpty(password)) {
      errors.password = i18n.t("password_is_required");
    }

    setErrors(errors);

    return isEmpty(errors);
  };

  const checkEmail = async () => {
    if (!isValid("username")) {
      return;
    }
    const { email } = getEmailAndAux();
    try {
      const res = await axios.post(`${NON_UPGRADED_USER_URL}/migrate-status`, {
        email
      });
      setIsMigrated(res.data.migrated);
      let options = [];
      let selected = {};
      const mobileRedirectUrl =
        query.get("callbackUrl") || localStorage.getItem("mobileRedirectUrl");
      if (
        (mobileRedirectUrl && !res.data.is_mobile) ||
        (!mobileRedirectUrl && !res.data.is_web)
      ) {
        setErrors({ migrateStatus: i18n.t("invalid_user") });
        return;
      }
      if (res.data.tenants.length > 0) {
        options = res.data.tenants.map(e => ({
          label: e.title,
          value: e.company || e.title,
          tenantId: e.tenant_id
        }));
        if (res.data.migrated && res.data.current) {
          selected = options.find(e => e.value === res.data.current.company);
        }
        if (localStorage.getItem("currentTenant")) {
          selected = options.find(
            e => e.value === Number(localStorage.getItem("currentTenant"))
          );
        }
        if (companyId) {
          selected = options.find(e => e.value === Number(companyId));
        }
        setTenantOptions(options);
        setSelectedTenant(selected?.value || options[0].value);
      }
      setIsTenantOpen(true);
    } catch (err) {
      setErrors({ migrateStatus: getApiErrorMsg(err) });
    }
  };

  const getEmailAndAux = () => {
    if (username.startsWith(SCS_EMAIL_ACCOUNT)) {
      return { email: username.split("/")[0], aux: "" };
    }

    let aux = username.includes("/") ? username.split("/")[0] : "";
    const email = aux ? username.replace(`${aux}/`, "") : username;

    if (
      isEmpty(aux) &&
      email.toLowerCase().includes(INTERNAL_EMAIL_ACCOUNT) &&
      !process.env.MOBILE_REVIEWER_ACCOUNT.includes(email.toLowerCase())
    ) {
      aux = TIS_AUX_TYPE;
    }

    return { email, aux };
  };

  const onSubmitTenant = async () => {
    if (isEmpty(selectedTenant) && !isValidNumber(selectedTenant)) {
      setErrors({
        tenant: i18n.t("field_is_required", { field: i18n.t("tenants") })
      });
      return;
    }

    // get company tenant config
    const { email, aux } = getEmailAndAux();
    let isSSOEnabled = false;
    if (isValidNumber(selectedTenant) && tenantId) {
      try {
        const res = await axios.post(
          `${NON_UPGRADED_COMPANY_URL}/${selectedTenant}/sso`,
          { email }
        );
        isSSOEnabled = res.data.ssoEnabled;
      } catch (err) {
        console.log("error get company sso", err.response);
      }
    }

    setIsTenantOpen(false);
    const mobileRedirectUrl =
      query.get("callbackUrl") || localStorage.getItem("mobileRedirectUrl");
    if (isMigrated && isSSOEnabled) {
      const redirectUrl = new Uri(
        `${window.location.origin}/verify`
      ).addQueryParam("tenant", selectedTenant);

      if (mobileRedirectUrl) {
        redirectUrl.addQueryParam("callbackUrl", mobileRedirectUrl);
      }

      if (query.get("source")) {
        redirectUrl.addQueryParam("source", query.get("source"));
      }

      if (query.get("applicationId")) {
        redirectUrl.addQueryParam("applicationId", query.get("applicationId"));
      }

      const tisLoginUrl = new Uri(getEnvConstant(GLOBAL_ENV.TIS_LOGIN_URL))
        .addQueryParam("environment", getEnvConstant(GLOBAL_ENV.TIS_ENVIRONMENT))
        .addQueryParam("returnUrl", redirectUrl.toString())
        .addQueryParam("email", email)
        .addQueryParam("tenantId", tenantId);

      if (aux) {
        tisLoginUrl.addQueryParam("aux", aux);
      }
      window.location.href = tisLoginUrl.toString();
      return;
    }
    let redirectUrl = null;
    if (selectedTenant === LoginTenant.LIVE && isPlayEnv()) {
      redirectUrl = new Uri(getEnvConstant(GLOBAL_ENV.LIVE_ENVIRONMENT))
        .setProtocol("https")
        .addQueryParam("email", username)
        .addQueryParam("tenant", selectedTenant);
    }
    if (selectedTenant === LoginTenant.PLAY && !isPlayEnv()) {
      redirectUrl = new Uri(getEnvConstant(GLOBAL_ENV.PLAY_ENVIRONMENT))
        .setProtocol("https")
        .addQueryParam("email", username)
        .addQueryParam("tenant", selectedTenant);
    }
    if (redirectUrl) {
      if (mobileRedirectUrl) {
        redirectUrl.addQueryParam("callbackUrl", mobileRedirectUrl);
      }
      if (query.get("source")) {
        redirectUrl.addQueryParam("source", query.get("source"));
      }
      window.location.href = redirectUrl.toString();
      return;
    }
    setStep(LOGIN_STEP.ENTER_PASSWORD);
  };

  const handleSubmit = async () => {
    if (!isValid()) {
      setPassword("");
      return;
    }

    setErrors({});
    setLoading(true);
    localStorage.removeItem("login-error");
    try {
      let postData = {
        email: username,
        password,
        source: getLoginSource(query.get("source"))
      } as LoginData;
      if (isValidNumber(selectedTenant)) {
        postData.company_id = selectedTenant as number;
      }
      await login(postData);
      const redirectUrl = new Uri(`${window.location.origin}/verify`);
      if (query.get("applicationId")) {
        redirectUrl.addQueryParam("applicationId", query.get("applicationId"));
      }
      if (callbackUrl) {
        redirectUrl
          .addQueryParam("tenant", selectedTenant)
          .addQueryParam("callbackUrl", callbackUrl);
      }
      window.location.href = redirectUrl.toString();
    } catch (err) {
      setLoading(false);
      setPassword("");

      if (isSCSGeneratePassword(err)) {
        generatePassword.setUrl(err.response.data.message);
        return;
      }

      const errorMessage = getLoginError(err) || loginError;
      if (
        callbackUrl &&
        errorMessage !==
          i18n.t(
            "invalid_credentials_your_account_will_be_locked_out_after_5_failed_attempts_please_try_again_or_check_your_email"
          )
      ) {
        window.location.href = new Uri(callbackUrl)
          .addQueryParam("error", errorMessage)
          .toString();
      }
    }
  };

  const onGoBack = () => {
    setError(undefined);
    setErrors({});
    localStorage.removeItem("login-error");
    setStep(LOGIN_STEP.ENTER_EMAIL);
  };

  const errorToDisplay =
    getLoginError(error) || loginError || errors.migrateStatus;

  const emailPasswordForm = (
    <form className="login_form" onSubmit={preventDefault(handleSubmit)}>
      <ConfirmModal
        isOpen={generatePassword.showGeneratePassword}
        header={i18n.t("do_you_want_to_generate_scs_password")}
        onConfirm={generatePassword.onConfirm}
        onCancel={generatePassword.onCancel}
      />
      {errorToDisplay && <LoginInfoDisplay errorMsg={errorToDisplay} />}
      <>
        <Input
          className={`email ${isPlayEnv() ? "play" : ""}`}
          field="email"
          id="email"
          label={i18n.t("email")}
          value={username}
          error={errors?.username}
          onChange={() => {}}
          inputProps={{ autoCapitalize: "off" }}
          required
          disabled
        />
        <Input
          className={`password ${isPlayEnv() ? "play" : ""}`}
          id="password"
          field="password"
          label={i18n.t("password")}
          ref={passwordInput}
          error={errors?.password}
          value={password}
          onChange={e => setPassword(e.target.value)}
          icon={{
            position: "right",
            render: () => (
              <PasswordVisibility
                visible={isPasswordVisible}
                onToggle={() => setIsPasswordVisible(!isPasswordVisible)}
              />
            )
          }}
          required
          type={isPasswordVisible ? "text" : "password"}
          disabled={isLoading}
        />
        <Button
          className="login-button"
          isLoading={isLoading}
          buttonProps={{ type: "submit" }}
        >
          {i18n.t("login")}
        </Button>

        <div className="button-link-container">
          <Link to="" className="login-link go-back" onClick={onGoBack}>
            <Icons.ArrowBack className="go-back-icon" />
            {i18n.t("go_back")}
          </Link>

          <Link to="/forgetpassword" className="login-link">
            {i18n.t("forgot_your_password")}
          </Link>
        </div>
      </>
    </form>
  );

  return (
    <>
      {step === LOGIN_STEP.ENTER_EMAIL && (
        <form className="login_form" onSubmit={preventDefault(checkEmail)}>
          {errorToDisplay && <LoginInfoDisplay errorMsg={errorToDisplay} />}
          <Input
            className={`email ${isPlayEnv() ? "play" : ""}`}
            field="email"
            id="email"
            label={i18n.t("email")}
            value={username}
            error={errors?.username}
            onChange={e => setUserName(e.target.value)}
            inputProps={{ autoCapitalize: "off" }}
            required
            disabled={isLoading}
          />
          <Button
            className="login-button"
            isLoading={isLoading}
            buttonProps={{ type: "submit" }}
          >
            {i18n.t("login")}
          </Button>
        </form>
      )}
      <Modal
        size="sm"
        isOpen={isTenantOpen}
        isLoading={isLoading}
        header={i18n.t("tenants")}
      >
        <Modal.Body>
          <Select
            label={i18n.t("tenants")}
            placeholder={i18n.t("select_tenants")}
            options={tenantOptions}
            value={selectedTenant}
            onChange={e => setSelectedTenant(e.target.value)}
            error={errors?.tenant}
          />
        </Modal.Body>
        <Modal.Footer
          saveButton
          saveButtonText={i18n.t("confirm")}
          saveButtonProps={{ icon: { render: Icons.Check } }}
          onSubmit={onSubmitTenant}
        />
      </Modal>
      {step === LOGIN_STEP.ENTER_PASSWORD && emailPasswordForm}
    </>
  );
};

export default LoginForm;
